import { useState, useEffect } from "react";

interface UserLocation {
  suburb: string;
  city: string;
  state: string;
  latitude?: number;
  longitude?: number;
}

const STORAGE_KEY = "wellnessconnect_user_location";

export const useUserLocation = () => {
  const [location, setLocation] = useState<UserLocation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Try to load from localStorage first
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        setLocation(JSON.parse(stored));
        setIsLoading(false);
        return;
      } catch {
        localStorage.removeItem(STORAGE_KEY);
      }
    }

    // Request geolocation
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            
            // Use OpenStreetMap Nominatim for reverse geocoding (free, no API key needed)
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1`,
              {
                headers: {
                  "User-Agent": "WellnessConnectAU/1.0",
                },
              }
            );
            
            if (response.ok) {
              const data = await response.json();
              const address = data.address || {};
              
              const newLocation: UserLocation = {
                suburb: address.suburb || address.neighbourhood || address.city_district || address.town || "",
                city: address.city || address.town || address.municipality || "",
                state: address.state || "",
                latitude,
                longitude,
              };
              
              setLocation(newLocation);
              localStorage.setItem(STORAGE_KEY, JSON.stringify(newLocation));
            }
          } catch (err) {
            console.error("Geocoding error:", err);
            setError("Could not determine location");
          } finally {
            setIsLoading(false);
          }
        },
        (err) => {
          console.error("Geolocation error:", err);
          setError("Location access denied");
          setIsLoading(false);
          
          // Set a default location
          const defaultLocation: UserLocation = {
            suburb: "Sydney CBD",
            city: "Sydney",
            state: "NSW",
          };
          setLocation(defaultLocation);
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        }
      );
    } else {
      setError("Geolocation not supported");
      setIsLoading(false);
    }
  }, []);

  const updateLocation = (newLocation: UserLocation) => {
    setLocation(newLocation);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newLocation));
  };

  const clearLocation = () => {
    setLocation(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  return {
    location,
    isLoading,
    error,
    updateLocation,
    clearLocation,
  };
};
